import { NavLink } from 'react-router-dom';
import { LayoutDashboard, Calendar, Scissors, LogOut, Clock, BookOpen, Users, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { theme } from '@/lib/theme';
import { useSidebarStore } from '@/hooks/useSidebarStore';

interface NavItem {
  label: string;
  icon: React.ReactNode;
  path: string;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', icon: <LayoutDashboard size={16} />, path: '/admin/dashboard' },
  { label: 'Citas', icon: <Calendar size={16} />, path: '/admin/citas' },
  { label: 'Clientes', icon: <Users size={16} />, path: '/admin/clientes' },
  { label: 'Servicios', icon: <Scissors size={16} />, path: '/admin/servicios' },
  { label: 'Contabilidad', icon: <BookOpen size={16} />, path: '/admin/accounting' },
  { label: 'Disponibilidad', icon: <Clock size={16} />, path: '/admin/disponibilidad' },
];

function SidebarContent({ collapsed, onClose, onToggleDesktop, isMobile }: {
  collapsed: boolean;
  onClose: () => void;
  onToggleDesktop: () => void;
  isMobile: boolean;
}) {
  const { logout } = useAuth();

  return (
    <aside
      className="h-full flex flex-col"
      style={{
        backgroundColor: theme.colors.bgSidebar,
        borderRight: `1px solid ${theme.colors.border}`,
      }}
    >
      {/* Logo + toggle */}
      <div
        className={`flex items-center h-16 flex-shrink-0 ${collapsed ? 'justify-center px-2' : 'px-4 justify-between'}`}
        style={{ borderBottom: `1px solid ${theme.colors.border}` }}
      >
        {!collapsed && (
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 flex items-center justify-center flex-shrink-0">
              <img
                src="https://storage.readdy-site.link/project_files/0d0d1fd6-befb-4f18-9f8b-efee19c55c96/fe9d8cd5-f344-4aec-86a2-924460c8dc34_LOGO-sin-letra.png?v=e571b3f4ddc76f76526cd8e946a6178b"
                alt="Barber Studio Luisfer"
                className="w-full h-full object-contain"
              />
            </div>
            <div className="min-w-0">
              <p className="text-white text-sm font-semibold leading-tight truncate" style={{ fontFamily: theme.fonts.display }}>
                Barber Studio
              </p>
              <p className="text-xs font-medium tracking-wider" style={{ color: theme.colors.textMuted }}>
                LUISFER
              </p>
            </div>
          </div>
        )}

        {collapsed && (
          <div className="w-8 h-8 flex items-center justify-center">
            <img
              src="https://storage.readdy-site.link/project_files/0d0d1fd6-befb-4f18-9f8b-efee19c55c96/fe9d8cd5-f344-4aec-86a2-924460c8dc34_LOGO-sin-letra.png?v=e571b3f4ddc76f76526cd8e946a6178b"
              alt="Logo"
              className="w-full h-full object-contain"
            />
          </div>
        )}

        {isMobile ? (
          <button
            onClick={onClose}
            className="flex w-7 h-7 items-center justify-center rounded-md transition-all hover:bg-white/8 flex-shrink-0"
            style={{ color: theme.colors.textDisabled }}
          >
            <X size={16} />
          </button>
        ) : (
          <button
            onClick={onToggleDesktop}
            className="flex w-7 h-7 items-center justify-center rounded-md transition-all hover:bg-white/8 flex-shrink-0"
            style={{ color: theme.colors.textDisabled }}
            title={collapsed ? 'Expandir sidebar' : 'Colapsar sidebar'}
          >
            {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className={`flex-1 py-4 space-y-0.5 overflow-y-auto ${collapsed ? 'px-2' : 'px-3'}`}>
        {!collapsed && (
          <p className="text-[10px] uppercase tracking-widest px-3 mb-3 font-medium" style={{ color: theme.colors.textDisabled }}>
            Menú
          </p>
        )}
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end
            onClick={isMobile ? onClose : undefined}
            title={collapsed ? item.label : undefined}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg text-sm transition-all cursor-pointer relative ${
                collapsed ? 'justify-center px-0 py-2.5' : 'px-3 py-2.5'
              } ${isActive ? '' : 'hover:bg-white/5'}`
            }
            style={({ isActive }) => ({
              backgroundColor: isActive ? 'rgba(255,255,255,0.07)' : 'transparent',
              color: isActive ? theme.colors.textPrimary : theme.colors.textSecondary,
              borderLeft: !collapsed && isActive ? `2px solid ${theme.colors.primary}` : '2px solid transparent',
            })}
          >
            {({ isActive }) => (
              <>
                <div
                  className="w-5 h-5 flex items-center justify-center flex-shrink-0"
                  style={{ color: isActive ? theme.colors.primary : theme.colors.textSecondary }}
                >
                  {item.icon}
                </div>
                {!collapsed && (
                  <>
                    <span className="font-medium">{item.label}</span>
                    {isActive && (
                      <div className="ml-auto w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: theme.colors.primary }} />
                    )}
                  </>
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Logout */}
      <div className={`py-4 ${collapsed ? 'px-2' : 'px-3'}`} style={{ borderTop: `1px solid ${theme.colors.border}` }}>
        <button
          onClick={logout}
          title={collapsed ? 'Cerrar sesión' : undefined}
          className={`w-full flex items-center gap-3 rounded-lg text-sm transition-all cursor-pointer group hover:bg-red-500/8 ${
            collapsed ? 'justify-center px-0 py-2.5' : 'px-3 py-2.5'
          }`}
          style={{ color: theme.colors.textDisabled }}
        >
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 group-hover:text-red-400 transition-colors">
            <LogOut size={15} />
          </div>
          {!collapsed && (
            <span className="font-medium group-hover:text-red-400 transition-colors">Cerrar sesión</span>
          )}
        </button>
      </div>
    </aside>
  );
}

export default function AdminSidebar() {
  const { desktopOpen, mobileOpen, toggleDesktop, closeMobile } = useSidebarStore();

  return (
    <>
      {/* Mobile overlay backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
          onClick={closeMobile}
        />
      )}

      {/* Mobile sidebar — fixed drawer that slides in */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 md:hidden transition-transform duration-300 ease-in-out ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <SidebarContent collapsed={false} onClose={closeMobile} onToggleDesktop={toggleDesktop} isMobile={true} />
      </div>

      {/* Desktop sidebar — inline, collapsible */}
      <div
        className={`hidden md:flex flex-col flex-shrink-0 min-h-screen transition-all duration-300 ease-in-out ${
          desktopOpen ? 'w-64' : 'w-16'
        }`}
      >
        <SidebarContent collapsed={!desktopOpen} onClose={closeMobile} onToggleDesktop={toggleDesktop} isMobile={false} />
      </div>
    </>
  );
}
